import { useLayoutContext } from '@/container/context/layout/layout-context';
import { useScreenSize } from '@shared/hooks/use-screen';
import { Layout } from 'antd';
import { lazy, LegacyRef, ReactNode, Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import { Breadcrumb } from './app-bread-crumb.types';
import { AppBreadcrumbs } from './components/app-bread-crumbs';
import AppHeader from './components/header/header.view';
import { Sidebar } from './components/sidebar/sidebar';
import { SubmenuOverlayPage } from './components/submenu-overlay/submenu-overlay.page';
import { subMenuRoutes } from './constants';
import { ClaimNumbersView } from '@modules/settings/my-numbers/features/claim-numbers/claim-numbers.view';
import { usePhoneStore } from '@modules/phone/store/phone.store';
import { useNotificationRedirection } from '@shared/hooks/use-notification-redirection';
import { useForegroundNotification } from '@shared/hooks/use-foreground-notification';
import { AppBanner } from './components/app-banner/app-banner';
import ChannelMigrateModal from '@modules/design-switch/channel-migrate.model';
const SubMenuSidebarPage = lazy(() => import('./components/submenu-sidebar/submenu-sidebar.page'));
const Phone = lazy(() => import('@modules/phone/phone.view'));

interface Props {
  children: ReactNode;
  breadcrumbs?: Breadcrumb[];
  pageSize?: 'small' | 'medium' | 'large';
}
export function AppLayout({ children, breadcrumbs, pageSize }: Props) {
  const { pathname } = useLocation();
  const { isMobile } = useScreenSize();
  const { endLayoutRef } = useLayoutContext();
  const { isOpen } = usePhoneStore();

  useForegroundNotification();
  useNotificationRedirection();

  const isExactSubmenuPath = subMenuRoutes.find((menu) => '/' + menu === pathname);
  const isConversationMenu = pathname.includes('/conversations') && pathname.split('/').length === 3;
  const isConversationPage = pathname.includes('/conversations') && pathname.split('/').length === 4;

  const mobileLayout =
    isExactSubmenuPath || isConversationMenu ? (
      <Suspense>
        <div className="w-full">
          <SubMenuSidebarPage />
        </div>
      </Suspense>
    ) : (
      <Layout className="flex-row! gap-0!">
        <div className="flex-1 flex flex-col h-dvh w-full">
          {/* hide header in conversation page */}
          {!isConversationPage && <AppHeader />}
          <AppBanner />
          <AppBreadcrumbs breadcrumbs={breadcrumbs ?? null} pageSize={pageSize} />
          <div className="flex-1 overflow-auto flex flex-col space-y-2 page-layout-scroll-container">{children}</div>
        </div>
        <div className="z-100 h-dvh absolute right-0 top-0" ref={endLayoutRef as LegacyRef<HTMLDivElement>} />
      </Layout>
    );

  return (
    <Layout hasSider className="max-w-full overflow-x-hidden">
      <Sidebar />
      <SubmenuOverlayPage />

      {isMobile ? (
        mobileLayout
      ) : (
        <Suspense>
          <SubMenuSidebarPage />
          <Layout className="h-screen overflow-hidden">
            <AppHeader />
            <div className="flex flex-1 overflow-hidden">
              <div className="flex flex-col flex-1 overflow-hidden">
                <AppBanner />
                <AppBreadcrumbs breadcrumbs={breadcrumbs ?? null} pageSize={pageSize} />
                <div className="flex-1 overflow-auto flex flex-col gap-2 page-layout-scroll-container">{children}</div>
              </div>
              <div ref={endLayoutRef as LegacyRef<HTMLDivElement>} />
            </div>
          </Layout>
        </Suspense>
      )}
      {isOpen && (
        <Suspense>
          <Phone />
        </Suspense>
      )}
      <ClaimNumbersView />
      <ChannelMigrateModal />
    </Layout>
  );
}
